import { CardMedia, CardContent, Typography, styled } from '@mui/material'
import {
  HBCard,
  HBCardActionArea,
  CardImageContainer,
  VenueInformationContainer,
  LocationChip,
  QuickDeclineButton,
  ImageOverlay,
  GoodFitChip,
} from './CardStyles'
import { HBButton } from './HBButton'
import { formatImageString } from '../../../Utilities/formatting'
import { useSearchParams } from 'react-router-dom'
import { NewChip } from '../Chips'
import { RelevancyFit } from '../../../generated-types'

const getVenueCardPath = (interestId: number): string => {
  const [searchParams] = useSearchParams()

  return `/responses/${interestId}?${searchParams.toString()}`
}

const ResponsiveCardMedia = styled(CardMedia, {
  shouldForwardProp: (propName) => propName !== 'size',
})<{ size?: 'large' | 'small' }>(({ theme, size }) => ({
  height: 224,

  [theme.breakpoints.up('sm')]: {
    height: size === 'small' ? 'clamp(160px, 24vw, 200px)' : '224px',
  },
  [theme.breakpoints.up('md')]: {
    height: size === 'small' ? '214px' : '224px',
  },
}))

export interface VenueCardProps {
  location?: string
  name: string
  matched?: boolean
  declined?: boolean
  timestampChip?: React.ReactNode
  buttonText: string
  has3d: boolean
  recommended: boolean
  imageUrl: string
  interestId: number
  uuid: string
  className?: string
  fit?: RelevancyFit
  size?: 'small' | 'large'
}

export interface VenueCardWithDeclineProps extends VenueCardProps {
  handleQuickDecline: () => void
  isTouchScreen: boolean
  showNewTag: boolean
}

const VenueCardWrapper = ({
  children,
  props,
}: {
  children?: React.ReactNode
  props: VenueCardProps | VenueCardWithDeclineProps
}): JSX.Element => {
  const showGoodFit = props.fit === 'good' && !props.timestampChip
  return (
    <HBCard className={props.className}>
      <HBCardActionArea disableRipple href={getVenueCardPath(props.interestId)}>
        {children && children}
        <CardContent style={{ padding: 0 }}>
          <Typography
            noWrap
            margin={'0.5rem 0 0'}
            fontSize='1.1rem'
            variant='h3'
            fontWeight={400}
          >
            {props.name}
          </Typography>
          <VenueInformationContainer>
            {props.timestampChip}
            {showGoodFit && <GoodFitChip />}
            {props.location && <LocationChip location={props.location} />}
          </VenueInformationContainer>
          <HBButton
            tabIndex={-1}
            className='main-card-button'
            variant='contained'
          >
            {props.buttonText}
          </HBButton>
        </CardContent>
      </HBCardActionArea>
    </HBCard>
  )
}

const VenueCardImage = ({
  children,
  imageUrl,
  size,
}: {
  children?: React.ReactNode
  imageUrl: string
  size?: 'small' | 'large'
}): JSX.Element => {
  return (
    <CardImageContainer>
      <ResponsiveCardMedia
        aria-hidden='true'
        size={size}
        image={formatImageString(imageUrl, 600)}
        style={{ borderRadius: '8px' }}
      />
      {children}
    </CardImageContainer>
  )
}

export const VenueCard = (props: VenueCardProps): JSX.Element => {
  return (
    <VenueCardWrapper props={props}>
      <VenueCardImage size={props.size} imageUrl={props.imageUrl} />
    </VenueCardWrapper>
  )
}

export const VenueCardWithQuickDecline = (
  props: VenueCardWithDeclineProps
): JSX.Element => {
  const { isTouchScreen, handleQuickDecline, showNewTag, imageUrl } = props

  return (
    <VenueCardWrapper props={props}>
      <QuickDeclineButton
        isTouchScreen={isTouchScreen}
        handleQuickDecline={handleQuickDecline}
      />
      <VenueCardImage size={props.size} imageUrl={imageUrl}>
        {showNewTag && (
          <ImageOverlay>
            <NewChip />
          </ImageOverlay>
        )}
      </VenueCardImage>
    </VenueCardWrapper>
  )
}
